/* import __COLOCATED_TEMPLATE__ from './fin-reply-addresses.hbs'; */
/* RESPONSIBLE TEAM: team-channels */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type DomainService from 'embercom/services/domain-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type FinReplyAddressService from 'embercom/services/fin-reply-address-service';
import type { Domain, DomainEmailAddress } from 'embercom/services/domain-service';
import { getDomainName } from 'embercom/lib/email';
const CAN_EDIT_SENDER_DOMAIN = 'can_settings__default_sender__manage';

interface Args {
  emailAddresses?: DomainEmailAddress[];
}

interface ReplyAddressItem {
  value: string | number;
  text: string;
  component: string;
  componentAttrs?: {
    verified?: boolean;
    isSelected?: boolean;
    isAuthenticated?: any;
    hasForwardingEnabled?: boolean;
  };
  isDisabled?: boolean;
}

export default class FinReplyAddresses extends Component<Args> {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare intercomEventService: any;
  @service declare domainService: DomainService;
  @service declare permissionsService: $TSFixMe;
  @service declare brandService: $TSFixMe;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare finReplyAddressService: FinReplyAddressService;

  @tracked hasPermission = false;
  @tracked selectedFinReplyEmailAddressId?: string;
  @tracked isLoading = true;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.hasPermission = this.permissionsService.currentAdminCan(CAN_EDIT_SENDER_DOMAIN);
    this.loadDefaultFinEmailId();
  }

  async loadDefaultFinEmailId() {
    try {
      this.isLoading = true;
      let defaultFinEmailId = await this.finReplyAddressService.fetchDefaultEmailAddressId();
      this.selectedFinReplyEmailAddressId = defaultFinEmailId
        ? String(defaultFinEmailId)
        : undefined;
    } catch (error) {
      console.error('Error loading Fin default email address', error);
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.email.customisation.fin-reply-addresses.load-error'),
      );
      this.selectedFinReplyEmailAddressId = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  get finEmailAddresses(): DomainEmailAddress[] {
    return this.args.emailAddresses || [];
  }

  get needsEmailAddress() {
    return (
      (this.finEmailAddresses.length === 0 || !this.hasVerifiedAndAuthenticatedAddress) &&
      !this.isLoading
    );
  }

  get hasVerifiedAndAuthenticatedAddress() {
    return this.verifiedEmailAddresses.length > 0;
  }

  get verifiedEmailAddresses(): DomainEmailAddress[] {
    return this.finEmailAddresses.filter((address) => address.verified);
  }

  get app() {
    return this.appService.app;
  }

  get showBrandsLink() {
    return this.brandService.isAppOnRightMultibrandPlan;
  }

  @action
  showPermissionsModal() {
    this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(CAN_EDIT_SENDER_DOMAIN);
  }

  get selectedFinReplyEmail(): string {
    let selectedEmail = this.finEmailAddresses.find(
      (email: DomainEmailAddress) => email.id === this.selectedFinReplyEmailAddressId,
    );
    return (
      selectedEmail?.email ||
      this.intl.t('new-settings.channels.email.customisation.fin-reply-addresses.select-address')
    );
  }

  @action
  async onSelectFinReplyEmailAddress(selectedItem: string): Promise<void> {
    this.selectedFinReplyEmailAddressId = selectedItem;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'selected',
      object: 'fin_default_reply_address_dropdown',
      place: 'fin-reply-address',
      context: 'email-settings',
    });
    await this.updateFinDefaultReplyAddress();
  }

  async updateFinDefaultReplyAddress() {
    try {
      await this.finReplyAddressService.updateDefaultEmailAddress(
        this.selectedFinReplyEmailAddressId,
      );

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.channels.email.customisation.fin-reply-addresses.update-success'),
      );
    } catch (error) {
      console.error('Error updating Fin default reply address', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t(
          'new-settings.channels.email.customisation.fin-reply-addresses.update-error',
        ),
      });
    }
  }

  isItemDisabled(address: DomainEmailAddress) {
    return (
      !this.isEmailDomainAuthenticated(address.email) ||
      !address.verified ||
      !address.forwardingEnabled
    );
  }

  isEmailDomainAuthenticated(email: string) {
    let emailDomain = getDomainName(email);
    return this.domainService.domains.find((domain: Domain) => domain.name === emailDomain)
      ?.isAuthenticated;
  }

  get finReplyAddressItems(): ReplyAddressItem[] {
    return this.finEmailAddresses.map((address: DomainEmailAddress) => ({
      value: address.id || '',
      text: address.email,
      isDisabled: this.isItemDisabled(address),
      component: 'new-settings/workspace/brands/brand-email-dropdown-item',
      componentAttrs: {
        verified: address.verified || false,
        id: address.id,
        isSelected: this.selectedFinReplyEmailAddressId === address.id,
        isAuthenticated: address.senderEmailAddressSettings
          ? this.isEmailDomainAuthenticated(address.senderEmailAddressSettings.email)
          : false,
        hasForwardingEnabled: address.forwardingEnabled || false,
      },
    }));
  }

  @action
  openBrandsSettingsPage() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'open_brands_settings_page',
      place: 'fin-reply-address',
      context: 'email-settings',
    });
    let url = this.router.urlFor('apps.app.settings.workspace.brands', this.appService.app.id);
    safeWindowOpen(url, '_blank');
  }

  @action
  navigateToDomainsAndAddresses() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'add_email_address',
      place: 'fin-reply-address',
      context: 'email-settings',
    });

    this.router.transitionTo('apps.app.settings.channels.email.index', this.appService.app.id, {
      queryParams: { step: 'add-address', tab: 'domains-and-addresses' },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Email::Customisation::FinReplyAddresses': typeof FinReplyAddresses;
  }
}
